import { useEffect } from "react";
import { Link } from "react-router-dom";

export default function ShippingPolicy() {
  useEffect(() => {
    document.title = "Shipping & Returns | Tablecure";
  }, []);

  const lastUpdated = "April 12, 2025";

  return (
    <div className="policy-page tc-page">
      <div className="pd2-bg">
        <div className="policy-orb policy-orb1" />
        <div className="policy-orb policy-orb2" />
        <div className="ck-grid" />
      </div>

      <div className="policy-inner">
        <div className="policy-header">
          <div className="pd2-eyebrow">
            <span className="pd2-eyebrow-dot" />
            Legal
          </div>
          <h1 className="policy-title">Shipping &amp; Returns</h1>
          <p className="policy-subtitle">Last updated: {lastUpdated}</p>
        </div>

        <div className="policy-body">

          {/* Quick-reference cards */}
          <div className="policy-cards">
            <div className="policy-card">
              <div className="policy-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              </div>
              <div className="policy-card-label">Free Shipping</div>
              <div className="policy-card-value">On all orders</div>
            </div>
            <div className="policy-card">
              <div className="policy-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="policy-card-label">Delivery</div>
              <div className="policy-card-value">5–8 business days</div>
            </div>
            <div className="policy-card">
              <div className="policy-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.51"/>
                </svg>
              </div>
              <div className="policy-card-label">Returns</div>
              <div className="policy-card-value">7-day window</div>
            </div>
            <div className="policy-card">
              <div className="policy-card-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div className="policy-card-label">Warranty</div>
              <div className="policy-card-value">2 years</div>
            </div>
          </div>

          <section className="policy-section">
            <h2>1. Order Processing</h2>
            <p>
              Orders are processed within <strong>1–2 business days</strong> of payment confirmation.
              You will receive an email with your order confirmation immediately after payment, and a
              second email with your tracking details once your order has been dispatched.
            </p>
            <p>
              Orders placed on weekends or public holidays are processed on the next business day.
            </p>
          </section>

          <section className="policy-section">
            <h2>2. Domestic Shipping (India)</h2>
            <p>
              We ship to all serviceable pin codes across India using trusted courier partners.
            </p>
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Estimated Delivery</th>
                  <th>Shipping Charge</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Metro cities (Mumbai, Delhi, Bangalore, Chennai, Kolkata, Hyderabad)</td>
                  <td>3–5 business days</td>
                  <td>Free</td>
                </tr>
                <tr>
                  <td>Tier 2 &amp; Tier 3 cities</td>
                  <td>5–8 business days</td>
                  <td>Free</td>
                </tr>
                <tr>
                  <td>Remote / hilly areas</td>
                  <td>7–12 business days</td>
                  <td>Free</td>
                </tr>
              </tbody>
            </table>
            <p>
              Delivery timelines are estimates and can vary due to courier delays, weather, or other
              factors outside our control.
            </p>
          </section>

          <section className="policy-section">
            <h2>3. International Shipping</h2>
            <p>
              We offer international shipping to most countries. Delivery times and any applicable
              customs duties or import taxes vary by destination and are the responsibility of the
              recipient.
            </p>
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Estimated Delivery</th>
                  <th>Shipping Charge</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>USA, UK, Canada, Australia</td>
                  <td>10–15 business days</td>
                  <td>Free</td>
                </tr>
                <tr>
                  <td>Europe</td>
                  <td>10–18 business days</td>
                  <td>Free</td>
                </tr>
                <tr>
                  <td>Rest of World</td>
                  <td>14–21 business days</td>
                  <td>Free</td>
                </tr>
              </tbody>
            </table>
            <p>
              <strong>Customs and import duties:</strong> International shipments may be subject to
              customs duties and taxes levied by the destination country. These charges are the
              customer's responsibility and are not included in the product price.
            </p>
          </section>

          <section className="policy-section">
            <h2>4. Order Tracking</h2>
            <p>
              Once your order ships, you will receive a tracking number via email. You can use this
              number to track your delivery on the courier partner's website. You can also view your
              order status in <Link to="/dashboard">My Account</Link>.
            </p>
          </section>

          <section className="policy-section">
            <h2>5. Returns Policy</h2>
            <p>
              We want you to love your Tablecure product. If you are not satisfied, you may return it
              within <strong>7 days of delivery</strong> subject to the conditions below.
            </p>
            <h3>Eligible returns</h3>
            <ul>
              <li>Item is unused, in its original condition, and in the original packaging.</li>
              <li>All accessories, manuals, and warranty cards are included.</li>
              <li>Return request is raised within 7 days of the delivery date.</li>
            </ul>
            <h3>Non-returnable items</h3>
            <ul>
              <li>Products showing signs of use, damage, or modification.</li>
              <li>Products with missing components or packaging.</li>
              <li>Items purchased during clearance or final-sale events.</li>
            </ul>
          </section>

          <section className="policy-section">
            <h2>6. How to Initiate a Return</h2>
            <ol>
              <li>Email us at <a href="mailto:support@tablecure.com">support@tablecure.com</a> within 7 days of delivery with your order number and reason for return.</li>
              <li>Our team will respond within 2 business days with return authorisation and instructions.</li>
              <li>Pack the item securely in its original packaging and ship it to the address provided.</li>
              <li>Share the return tracking number with us once dispatched.</li>
            </ol>
            <p>
              <strong>Return shipping:</strong> If the return is due to a manufacturing defect or an
              error on our part, we will cover the return shipping cost. For all other reasons, the
              customer bears the return shipping charge.
            </p>
          </section>

          <section className="policy-section">
            <h2>7. Refunds</h2>
            <p>
              Once we receive and inspect the returned item, we will notify you of the approval or
              rejection of your refund within 2 business days.
            </p>
            <p>
              Approved refunds are processed to the <strong>original payment method within
              5–10 business days</strong>. Depending on your bank or card issuer, it may take
              additional time for the refund to appear on your statement.
            </p>
          </section>

          <section className="policy-section">
            <h2>8. Damaged or Defective Items</h2>
            <p>
              If your order arrives damaged or defective, please email us at{" "}
              <a href="mailto:support@tablecure.com">support@tablecure.com</a> within 48 hours of
              delivery with:
            </p>
            <ul>
              <li>Your order number.</li>
              <li>A clear description of the issue.</li>
              <li>Photographs of the damaged product and packaging.</li>
            </ul>
            <p>
              We will arrange a replacement or full refund at no cost to you.
            </p>
          </section>

          <section className="policy-section">
            <h2>9. Warranty Claims</h2>
            <p>
              All Tablecure products come with a <strong>2-year warranty</strong> against
              manufacturing defects. To raise a warranty claim, contact{" "}
              <a href="mailto:support@tablecure.com">support@tablecure.com</a> with your proof of
              purchase and a description of the defect.
            </p>
          </section>

          <section className="policy-section">
            <h2>10. Contact</h2>
            <p>
              For any shipping or returns questions, please reach out:
            </p>
            <div className="policy-contact-block">
              <p><strong>Tablecure Customer Support</strong></p>
              <p>Email: <a href="mailto:support@tablecure.com">support@tablecure.com</a></p>
              <p>Website: <Link to="/contact">Contact Form</Link></p>
              <p>Response time: within 2 business days</p>
            </div>
          </section>

        </div>

        <div className="policy-footer-nav">
          <Link to="/" className="pd2-btn-ghost">← Back to Home</Link>
          <div className="policy-footer-links">
            <Link to="/privacy-policy" className="policy-footer-link">Privacy Policy</Link>
            <span className="policy-footer-sep" />
            <Link to="/terms" className="policy-footer-link">Terms of Service</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
